import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';

export default function AdminNotifications() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: '', message: '', type: 'promotion', sendEmailToo: false });
  const [sending, setSending] = useState(false);

  const fetchList = () => {
    api.get('/notifications/my')
      .then((res) => setList(res.data || []))
      .catch(() => {});
  };

  useEffect(fetchList, []);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/notifications/broadcast', form);
      toast.success('Broadcast notification dispatched to customers');
      setForm({ title: '', message: '', type: 'promotion', sendEmailToo: false });
      fetchList();
      window.dispatchEvent(new Event('notifications:refresh'));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send broadcast');
    } finally {
      setSending(false);
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'promotion':
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-50 text-purple-700 border border-purple-200">Promotion</span>;
      case 'stock':
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">Stock Notice</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200">System Alert</span>;
    }
  };

  return (
    <div className="space-y-6 text-[#2B1810] max-w-4xl">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Customer Notifications & Broadcasts</h1>
          <p className="text-xs text-[#8A796C]">Send promotional offers, stock refill alerts, and announcements to customer accounts.</p>
        </div>
        <div className="w-10 h-10 rounded-xl bg-[#FAF8F5] border border-[#E8DFD8] flex items-center justify-center text-[#C49A45]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        </div>
      </div>

      <form onSubmit={send} className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2 border-b border-[#E8DFD8] pb-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2"><path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
          <h3 className="font-heading text-base font-bold text-[#2B1810]">Create Broadcast Message</h3>
        </div>

        <div>
          <label className="label">Headline Title</label>
          <input
            required
            className="input-field"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Grand Festive Offer: 20% Off Authentic Sandalwood Malas!"
          />
        </div>

        <div>
          <label className="label">Message Body</label>
          <textarea
            required
            rows={3}
            className="input-field"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Enter full broadcast details and instructions for customers..."
          />
        </div>

        <div>
          <label className="label">Notification Category</label>
          <select
            className="input-field"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            <option value="promotion">Promotion & Special Discounts</option>
            <option value="system">Store & System Announcement</option>
            <option value="stock">Inventory & Stock Refill Notice</option>
          </select>
        </div>

        <label className="flex items-center gap-2.5 text-xs font-semibold text-[#2B1810] bg-[#FAF8F5] p-3 rounded-lg border border-[#E8DFD8] cursor-pointer">
          <input
            type="checkbox"
            className="accent-[#C49A45] w-4 h-4 rounded"
            checked={form.sendEmailToo}
            onChange={(e) => setForm({ ...form, sendEmailToo: e.target.checked })}
          />
          <span>Also dispatch as direct Email Broadcast to all active customers (via SMTP)</span>
        </label>

        <div className="pt-2 flex justify-end">
          <button
            disabled={sending}
            className="bg-[#C49A45] hover:bg-[#B08736] disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider px-8 py-3 rounded-lg shadow transition-all hover:scale-[1.02] inline-flex items-center gap-2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            {sending ? 'DISPATCHING BROADCAST...' : 'SEND BROADCAST NOTIFICATION'}
          </button>
        </div>
      </form>

      <div className="bg-white border border-[#E8DFD8] rounded-xl overflow-hidden shadow-sm">
        <div className="bg-[#FAF8F5] p-4 border-b border-[#E8DFD8] flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold text-[#2B1810]">Recent Broadcast & Alert History</h3>
          <span className="text-[10px] text-[#8A796C] font-semibold">{list.length} Total Messages</span>
        </div>

        <div className="divide-y divide-[#E8DFD8]">
          {list.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#8A796C]">
              No broadcast notifications have been sent yet.
            </div>
          ) : (
            list.map((n) => (
              <div key={n._id} className="p-4 hover:bg-[#FAF8F5] transition-colors space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {getTypeBadge(n.type)}
                    <p className="font-bold text-xs text-[#2B1810]">{n.title}</p>
                  </div>
                  <span className="text-[10px] text-[#8A796C] shrink-0 font-medium">
                    {new Date(n.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
                <p className="text-xs text-[#6E5D53] leading-relaxed pl-1">{n.message}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
